import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Fab } from '@mui/material';
import { Print as PrintIcon } from '@mui/icons-material';
import { useMedicationCart } from '../contexts/MedicationCartContext';
import { useAuth } from '../contexts/AuthContext';
import { medicationsHome } from '../lib/roleHome';
import { UserRole } from '@lysi-web/shared-types';

const EMPTY_ROWS = 3;

/* ── Notebook ruled-line background ─────────────────────────── */
const LINE_HEIGHT = 32; // px per ruled line
const MARGIN_LEFT = 48; // red margin line offset

const notebookBg = {
  background: `
    repeating-linear-gradient(
      transparent,
      transparent ${LINE_HEIGHT - 1}px,
      #c8d8e4 ${LINE_HEIGHT - 1}px,
      #c8d8e4 ${LINE_HEIGHT}px
    )
  `,
  backgroundSize: `100% ${LINE_HEIGHT}px`,
  backgroundPosition: `0 0`,
};

export const MedicationPrint: React.FC = () => {
  const { items, itemCount } = useMedicationCart();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lookupPath = medicationsHome(user?.role);

  // A consultant is pharmaceutical-lab staff leaving a product list with a doctor,
  // not a prescriber. The sheet must not present itself as a "Receta", and it has no
  // patient to name — the doctor filling it in is the one who prescribes.
  const isConsultant = user?.role === UserRole.CONSULTANT;
  const title = isConsultant ? 'Lista de Medicamentos' : 'Receta';
  const subtitle = isConsultant
    ? user?.pharmaceuticalLabName || 'Productos Consultados'
    : 'Lista de Medicamentos';
  const fieldLabels = isConsultant ? ['DOCTOR', 'FECHA'] : ['DOCTOR', 'FECHA', 'PACIENTE'];

  useEffect(() => {
    if (itemCount === 0) {
      navigate(lookupPath, { replace: true });
    }
  }, [itemCount, navigate, lookupPath]);

  if (itemCount === 0) return null;

  return (
    <>
      <Box
        sx={{
          maxWidth: 680,
          mx: 'auto',
          p: { xs: 1.5, md: 4 },
          pb: { xs: 12, md: 4 },
          '@media print': { p: 0, maxWidth: 'none' },
        }}
      >
        {/* Back link */}
        <Typography
          onClick={() => navigate(lookupPath)}
          sx={{
            fontFamily: '"Georgia", serif',
            fontSize: 13,
            color: '#b0bec5',
            cursor: 'pointer',
            mb: 1.5,
            display: 'inline-block',
            '&:hover': { color: '#2c3e50' },
            '@media print': { display: 'none' },
          }}
        >
          &larr; Volver
        </Typography>

        {/* ── Paper sheet ─────────────────────────────────── */}
        <Box
          className="printable-notepad"
          sx={{
            position: 'relative',
            backgroundColor: '#fdfaf5',
            borderRadius: { xs: '4px', md: '6px' },
            boxShadow: {
              xs: '0 1px 4px rgba(0,0,0,0.08)',
              md: '0 2px 16px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)',
            },
            overflow: 'hidden',
            '@media print': {
              boxShadow: 'none',
              borderRadius: 0,
              backgroundColor: '#fff',
            },
          }}
        >
          {/* Red margin line */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: { xs: 36, md: MARGIN_LEFT },
              width: '1.5px',
              background: 'rgba(205,160,160,0.35)',
              zIndex: 1,
              '@media print': { left: MARGIN_LEFT },
            }}
          />

          {/* ── Header strip ───────────────────────────────── */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              px: { xs: 2.5, md: 4 },
              pt: { xs: 3, md: 4 },
              pb: 1.5,
              borderBottom: '2px solid #b0bec5',
              '@media print': { px: 4 },
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Georgia", "Palatino Linotype", "Book Antiqua", serif',
                fontSize: { xs: 22, md: 26 },
                fontWeight: 700,
                letterSpacing: 3,
                textTransform: 'uppercase',
                color: '#2c3e50',
                textAlign: 'center',
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Georgia", serif',
                fontSize: 11,
                color: '#7f8c8d',
                textAlign: 'center',
                mt: 0.25,
                letterSpacing: 1,
                textTransform: 'uppercase',
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* ── Lined body ─────────────────────────────────── */}
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              ...notebookBg,
              px: { xs: 2.5, md: 4 },
              pt: `${LINE_HEIGHT}px`,
              '@media print': { px: 4 },
            }}
          >
            {/* Doctor / Patient / Date fields */}
            <Box sx={{ pl: { xs: '20px', md: '20px' }, mb: `${LINE_HEIGHT * 0.25}px` }}>
              {fieldLabels.map((label) => (
                <Box
                  key={label}
                  sx={{
                    height: LINE_HEIGHT,
                    display: 'flex',
                    alignItems: 'flex-end',
                    pb: '5px',
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: 11,
                      color: '#b0bec5',
                      letterSpacing: 1,
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    {label}:
                  </Typography>
                  <Box
                    sx={{
                      flex: 1,
                      borderBottom: '1px solid #b0bec5',
                      mb: '2px',
                    }}
                  />
                </Box>
              ))}
            </Box>

            {/* ── Medication list ──────────────────────────── */}
            <Box sx={{ pl: { xs: '20px', md: '20px' } }}>
              {items.map((item, index) => (
                <Box
                  key={item.pharmacyProductId}
                  sx={{
                    minHeight: LINE_HEIGHT * 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                    pb: '4px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                    <Typography
                      sx={{
                        fontFamily: '"Georgia", serif',
                        fontSize: 13,
                        color: '#b0bec5',
                        fontWeight: 700,
                        minWidth: 20,
                      }}
                    >
                      {index + 1}.
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"Courier New", "Courier", monospace',
                        fontSize: { xs: 13, md: 14 },
                        fontWeight: 700,
                        color: '#2c3e50',
                        wordBreak: 'break-word',
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                  {/* Sub-fields: Cant / Frecuencia / Duración as lined blanks */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: { xs: 1, md: 3 },
                      pl: '30px',
                      mt: '2px',
                    }}
                  >
                    {['Cant.', 'Frecuencia', 'Duración'].map((label) => (
                      <Typography
                        key={label}
                        sx={{
                          fontFamily: '"Georgia", serif',
                          fontSize: 11,
                          color: '#95a5a6',
                        }}
                      >
                        {label}: ___________
                      </Typography>
                    ))}
                  </Box>
                </Box>
              ))}

              {/* Empty rows for handwritten additions */}
              {Array.from({ length: EMPTY_ROWS }).map((_, i) => (
                <Box
                  key={`empty-${i}`}
                  sx={{
                    minHeight: LINE_HEIGHT * 2,
                    display: 'flex',
                    alignItems: 'flex-end',
                    pb: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: 13,
                      color: '#b0bec5',
                      fontWeight: 700,
                      minWidth: 20,
                    }}
                  >
                    {items.length + i + 1}.
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* ── Additional Instructions ──────────────────── */}
            <Box sx={{ pl: { xs: '20px', md: '20px' }, mt: `${LINE_HEIGHT * 0.5}px` }}>
              <Typography
                sx={{
                  fontFamily: '"Georgia", serif',
                  fontSize: 11,
                  color: '#b0bec5',
                  fontWeight: 700,
                  letterSpacing: 1,
                  mb: '4px',
                }}
              >
                INSTRUCCIONES ADICIONALES
              </Typography>
              {/* Three blank ruled lines */}
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{ height: LINE_HEIGHT }} />
              ))}
            </Box>

            {/* ── Signature ────────────────────────────────── */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: `${LINE_HEIGHT}px`,
                pb: `${LINE_HEIGHT * 1.5}px`,
              }}
            >
              <Box sx={{ textAlign: 'center', width: 220 }}>
                <Box sx={{ borderTop: '1px solid #2c3e50', pt: '6px' }}>
                  <Typography
                    sx={{
                      fontFamily: '"Georgia", serif',
                      fontSize: 11,
                      color: '#b0bec5',
                      letterSpacing: 1,
                    }}
                  >
                    FIRMA DEL DOCTOR
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ── Print FAB ─────────────────────────────────────── */}
      <Fab
        onClick={() => window.print()}
        aria-label="Imprimir"
        sx={{
          position: 'fixed',
          bottom: { xs: 'calc(72px + env(safe-area-inset-bottom))', md: 28 },
          right: { xs: 16, md: 28 },
          background: '#2c3e50',
          color: '#fdfaf5',
          width: 56,
          height: 56,
          boxShadow: '0 4px 14px rgba(44,62,80,0.35)',
          zIndex: (theme) => theme.zIndex.appBar + 1,
          '&:hover': { background: '#1a252f' },
          '@media print': { display: 'none' },
        }}
      >
        <PrintIcon />
      </Fab>
    </>
  );
};
